import Component from '@glimmer/component';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import t from 'ember-intl/helpers/t';
import { or } from 'ember-truth-helpers';
import { isPresent } from '@ember/utils';
import { DateTime } from 'luxon';
import { task, timeout } from 'ember-concurrency';
import CopyButton from '../copy-button';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import { on } from '@ember/modifier';
import pipe from '../../helpers/pipe';
import DatePicker from '../date-picker';
import perform from 'ember-concurrency/helpers/perform';
import LoadingSpinner from '../loading-spinner';
import { faCopy, faXmark } from '@fortawesome/free-solid-svg-icons';
import BigSaveCancelButtons from '../big-save-cancel-buttons';

export default class UserProfileApiTokenComponent extends Component {
  @service flashMessages;
  @service iliosConfig;
  @service fetch;
  @service session;
  @service intl;

  @tracked expiresAt = null;
  @tracked generatedJwt = null;
  @tracked maxDate = null;
  @tracked minDate = null;

  get apiDocsLink() {
    const apiPath = '/' + this.iliosConfig.apiNameSpace;
    const host = this.iliosConfig.apiHost
      ? this.iliosConfig.apiHost
      : window.location.protocol + '//' + window.location.host;
    const docPath = host + apiPath.replace('v3', 'doc');
    return `<a href="${docPath}">${docPath}</a>`;
  }

  constructor() {
    super(...arguments);
    this.reset();
  }

  tokenCopied = () => {
    this.flashMessages.success(this.intl.t('general.copiedSuccessfully'));
  };

  selectExpiresAtDate = (selectedDate) => {
    this.expiresAt = selectedDate;
  };

  reset = () => {
    const midnightToday = DateTime.fromObject({
      hour: 23,
      minute: 59,
      second: 59,
    });
    const twoWeeksFromNow = midnightToday.plus({ weeks: 2 });
    const nintyDaysFromNow = midnightToday.plus({ days: 90 });
    this.minDate = midnightToday.toJSDate();
    this.maxDate = nintyDaysFromNow.toJSDate();
    this.expiresAt = twoWeeksFromNow.toJSDate();
    this.generatedJwt = null;
  };

  createNewToken = task(async () => {
    await timeout(1); //small delay to allow rendering the spinner

    //set the expiration time to the end of the day
    const expiresAt = DateTime.fromJSDate(this.expiresAt).set({ hour: 23, minute: 59, second: 59 });
    //calculate the difference between now and the expiration time, and round each unit down (so we don't get secionds like 31.123)
    const diff = expiresAt.diffNow(['days', 'hours', 'minutes', 'seconds']).mapUnits(Math.floor);
    //Use Luxon's ISO format to get a string like "P1DT2H3M4S" which is 1 day, 2 hours, 3 minutes, and 4 seconds
    const interval = diff.toISO();

    const url = '/auth/token?ttl=' + interval;
    const data = await this.fetch.getJsonFromApiHost(url);
    this.generatedJwt = data.jwt;
  });

  invalidateTokens = task(async () => {
    await timeout(10); //small delay to allow rendering the spinner
    const url = '/auth/invalidatetokens';
    const data = await this.fetch.getJsonFromApiHost(url);

    if (isPresent(data.jwt)) {
      const session = this.session;
      const authenticator = 'authenticator:ilios-jwt';
      session.authenticate(authenticator, { jwt: data.jwt });
      this.flashMessages.success(this.intl.t('general.successfullyInvalidatedTokens'));
      this.args.toggleShowInvalidateTokens();
    }
  });

  getGeneratedJwtToken = () => {
    return this.generatedJwt;
  };
  <template>
    <div class="large-component api-token" data-test-token-maintenance>
      <h3>
        {{t "general.manageAPITokens"}}
      </h3>
      <p data-test-token-info-link>
        {{! template-lint-disable no-triple-curlies }}
        {{{t "general.tokenInfo" apiDocsLink=this.apiDocsLink}}}
      </p>
      {{#if (or @showInvalidateTokens @showCreateNewToken)}}
        {{#if @showCreateNewToken}}
          {{#if this.generatedJwt}}
            <div class="new-token-result" data-test-new-token-result>
              <label>
                {{t "general.newToken"}}:
                <input readonly value={{this.generatedJwt}} />
              </label>
              <CopyButton
                @getClipboardText={{this.getGeneratedJwtToken}}
                @success={{this.tokenCopied}}
                title={{t "general.copyNewToken"}}
              >
                <FaIcon @icon={{faCopy}} />
              </CopyButton>
              <button
                type="button"
                class="bigcancel"
                aria-label={{t "general.cancel"}}
                data-test-result-reset
                {{on "click" (pipe @toggleShowCreateNewToken this.reset)}}
              >
                <FaIcon @icon={{faXmark}} />
              </button>
            </div>
          {{else}}
            <div class="new-token-form" data-test-new-token-form>
              <label>
                {{t "general.validUntil"}}
              </label>
              <DatePicker
                @value={{this.expiresAt}}
                @maxDate={{this.maxDate}}
                @minDate={{this.minDate}}
                @onChange={{this.selectExpiresAtDate}}
              />
              <BigSaveCancelButtons
                @save={{this.createNewToken}}
                @cancel={{pipe @toggleShowCreateNewToken this.reset}}
              />
            </div>
          {{/if}}
        {{/if}}
        {{#if @showInvalidateTokens}}
          <div class="invalidate-tokens-form" data-test-invalidate-tokens-form>
            <h3>
              {{t "general.invalidateTokens"}}
            </h3>
            <p>
              {{t "general.invalidateTokensConfirmation"}}
            </p>
            <button
              type="button"
              class="done text"
              data-test-invalidate-tokens-submit
              {{on "click" (perform this.invalidateTokens)}}
            >
              {{#if this.invalidateTokens.isRunning}}
                <LoadingSpinner />
              {{else}}
                {{t "general.yes"}}
              {{/if}}
            </button>
            <button
              type="button"
              class="cancel text"
              data-test-invalidate-tokens-cancel
              {{on "click" @toggleShowInvalidateTokens}}
            >
              {{t "general.cancel"}}
            </button>
          </div>
        {{/if}}
      {{else}}
        <button
          type="button"
          class="new-token done text"
          data-test-show-create-new-token
          {{on "click" @toggleShowCreateNewToken}}
        >
          {{t "general.createNew"}}
        </button>
        <button
          type="button"
          class="invalidate-tokens cancel text"
          data-test-show-invalidate-tokens
          {{on "click" @toggleShowInvalidateTokens}}
        >
          {{t "general.invalidateTokens"}}
        </button>
      {{/if}}
    </div>
  </template>
}
